// Runner de migracion por PRAGMA user_version (WDUR-03).
//
// Los motores WASM (sqlite-wasm / wa-sqlite) NO tienen el addUpgradeStatement del plugin
// nativo. UNA sola fuente logica de esquema (schema.js V1_STATEMENTS), dos appliers:
//   - nativo: addUpgradeStatement(upgradeStatements)  (StorageAdapter, sin tocar)
//   - web:    esta escalera user_version
//
// schema.js / migrations.js siguen siendo modulos PUROS (cero import de motor): este
// runner los importa (V1_STATEMENTS de schema, V2_STATEMENTS + DB_VERSION de migrations), no al reves.
//
// Idempotente: si user_version >= DB_VERSION no hace nada. Si no, sube por la ESCALERA paso-a-paso
// (cada step aplica SOLO su delta en una tx y sella la version LITERAL de ESE step). Cambios futuros =
// nueva entrada toVersion:N en migrations.js + un step `if (cur === N-1) { ...; sella N }` aqui.

import { V1_STATEMENTS } from './schema'
import { V2_STATEMENTS, V3_STATEMENTS, V5_STATEMENTS, V7_STATEMENTS, DB_VERSION } from './migrations'

/**
 * @param {import('./WebSqliteBackend').WebSqliteBackend} backend
 */
export async function runUserVersionMigration (backend) {
  const r = await backend.query('PRAGMA user_version')
  // Tolera ambos shapes: { user_version: N } (rowMode object) o [N] (posicional).
  const row = r.values?.[0]
  let cur = row?.user_version ?? (Array.isArray(row) ? row[0] : undefined) ?? 0

  if (cur >= DB_VERSION) return // idempotente: high-water guard, nada que hacer.

  // ── ESCALERA POR STEP (no saltar versiones) ──────────────────────────────────────────────────
  // CRITICO: cada step sella su version LITERAL, NO DB_VERSION. Sellar DB_VERSION tras V1 en una
  // instalacion NUEVA (cur=0) saltaria V2 (la DB quedaria en user_version=2 sin los indices v2), y
  // regresionaria al agregar un V3 futuro. DB_VERSION es SOLO el guard de early-return de arriba.

  if (cur === 0) {
    // v0 → v1: crea TODO el esquema v1 en UNA tx (atomico: o todo o nada). Sella LITERAL 1.
    await backend.transaction(async (db) => {
      for (const stmt of V1_STATEMENTS) await db.execute(stmt)
    })
    await backend.execute('PRAGMA user_version = 1')
    cur = 1
  }

  if (cur === 1) {
    // v1 → v2: aplica SOLO el delta v2 (indices keyset) en UNA tx. Sella LITERAL 2.
    await backend.transaction(async (db) => {
      for (const stmt of V2_STATEMENTS) await db.execute(stmt)
    })
    await backend.execute('PRAGMA user_version = 2')
    cur = 2
  }

  if (cur === 2) {
    // v2 → v3: indice outbox(client_uuid, status) — purga de fotos / guard LWW / poda. Sella LITERAL 3.
    await backend.transaction(async (db) => {
      for (const stmt of V3_STATEMENTS) await db.execute(stmt)
    })
    await backend.execute('PRAGMA user_version = 3')
    cur = 3
  }

  if (cur === 3) {
    // v3 → v4: outbox.user_id (dueño de la op) para que el drain filtre por usuario en devices
    // compartidos. ALTER ADD COLUMN NO es idempotente -> chequear antes con table_info (crash-safe:
    // si murió tras el ALTER y antes del sello, la próxima apertura ve la columna y NO re-ALTERa). En
    // instalaciones nuevas la columna YA viene en V1_STATEMENTS, así que aquí solo se salta el ALTER.
    const info = await backend.query('PRAGMA table_info(outbox)')
    const cols = (info.values || []).map(c => (c && c.name) ?? (Array.isArray(c) ? c[1] : undefined))
    if (!cols.includes('user_id')) {
      await backend.transaction(async (db) => {
        await db.execute('ALTER TABLE outbox ADD COLUMN user_id INTEGER')
      })
    }
    await backend.execute('PRAGMA user_version = 4')
    cur = 4
  }

  if (cur === 4) {
    // v4 → v5: índice de la FK de c_catalog_elements (única sin índice). Sella LITERAL 5.
    await backend.transaction(async (db) => {
      for (const stmt of V5_STATEMENTS) await db.execute(stmt)
    })
    await backend.execute('PRAGMA user_version = 5')
    cur = 5
  }

  if (cur === 5) {
    // v5 → v6: sync_notifications.user_id (dueño de la nota) para el scoping por usuario del drawer.
    // ALTER condicional (mismo patrón crash-safe que v4): en instalaciones nuevas la columna ya viene
    // en V1_STATEMENTS -> solo se salta el ALTER. Sella LITERAL 6.
    const info = await backend.query('PRAGMA table_info(sync_notifications)')
    const cols = (info.values || []).map(c => (c && c.name) ?? (Array.isArray(c) ? c[1] : undefined))
    if (!cols.includes('user_id')) {
      await backend.transaction(async (db) => {
        await db.execute('ALTER TABLE sync_notifications ADD COLUMN user_id INTEGER')
      })
    }
    await backend.execute('PRAGMA user_version = 6')
    cur = 6
  }

  if (cur === 6) {
    // v6 → v7: índice de sync_notifications por usuario (dead-letter drawer / listPhotoIssues sin él
    // eran full scan). Índice net-new puro (mismo patrón que v2/v3/v5, sin ALTER -> sin chequeo
    // condicional). Sella LITERAL 7.
    await backend.transaction(async (db) => {
      for (const stmt of V7_STATEMENTS) await db.execute(stmt)
    })
    await backend.execute('PRAGMA user_version = 7')
    cur = 7
  }

  if (cur === 7) {
    // v7 → v8: outbox.done_at (retención de binarios tras el done — ver migrations.js). ALTER
    // condicional (mismo patrón crash-safe que v4/v6): en instalaciones nuevas la columna ya viene
    // en V1_STATEMENTS -> solo se salta el ALTER. Sella LITERAL 8.
    const info8 = await backend.query('PRAGMA table_info(outbox)')
    const cols8 = (info8.values || []).map(c => (c && c.name) ?? (Array.isArray(c) ? c[1] : undefined))
    if (!cols8.includes('done_at')) {
      await backend.transaction(async (db) => {
        await db.execute('ALTER TABLE outbox ADD COLUMN done_at INTEGER')
      })
    }
    await backend.execute('PRAGMA user_version = 8')
    cur = 8
  }

  if (cur === 8) {
    // v8 → v9: photo_meta.byte_length (integridad del binario — ver migrations.js). ALTER condicional
    // (mismo patrón crash-safe que v4/v6/v8). Sella LITERAL 9.
    const info9 = await backend.query('PRAGMA table_info(photo_meta)')
    const cols9 = (info9.values || []).map(c => (c && c.name) ?? (Array.isArray(c) ? c[1] : undefined))
    if (!cols9.includes('byte_length')) {
      await backend.transaction(async (db) => {
        await db.execute('ALTER TABLE photo_meta ADD COLUMN byte_length INTEGER')
      })
    }
    await backend.execute('PRAGMA user_version = 9')
    cur = 9
  }

  if (cur === 9) {
    // v9 → v10: signatures.signed_by/signed_name/is_auditor/is_audited/audited_rol_name/audited_name
    // (overlay de re-entrada de firmas, ISS-994, ver migrations.js). ALTER condicional (mismo patrón
    // crash-safe que v4/v6/v8/v9): en instalaciones nuevas las columnas ya vienen en V1_STATEMENTS ->
    // solo se saltan los ALTER que ya existan. Sella LITERAL 10.
    const info10 = await backend.query('PRAGMA table_info(signatures)')
    const cols10 = new Set((info10.values || []).map(c => c?.name ?? (Array.isArray(c) ? c[1] : undefined)))
    const newCols10 = [
      ['signed_by', 'INTEGER'],
      ['signed_name', 'TEXT'],
      ['is_auditor', 'INTEGER'],
      ['is_audited', 'INTEGER'],
      ['audited_rol_name', 'TEXT'],
      ['audited_name', 'TEXT']
    ].filter(([name]) => !cols10.has(name))
    if (newCols10.length) {
      await backend.transaction(async (db) => {
        for (const [name, type] of newCols10) {
          await db.execute(`ALTER TABLE signatures ADD COLUMN ${name} ${type}`)
        }
      })
    }
    await backend.execute('PRAGMA user_version = 10')
    cur = 10
  }

  if (cur === 10) {
    // v10 → v11: occurrences.comments (comentarios adicionales de la ocurrencia, F-97, ver migrations.js).
    // ALTER condicional (mismo patrón crash-safe que v4/v6/v8/v9/v10): en instalaciones nuevas la columna
    // ya viene en V1_STATEMENTS -> solo se salta el ALTER. Sella LITERAL 11.
    // v11 es hoy el último step de la escalera: sin step v12 todavía, sellar `cur` aquí sería un
    // assignment muerto (Sonar S1854). Cuando se agregue v12, agregar `cur = 11` al copiar este patrón.
    const info11 = await backend.query('PRAGMA table_info(occurrences)')
    const cols11 = (info11.values || []).map(c => c?.name ?? (Array.isArray(c) ? c[1] : undefined))
    if (!cols11.includes('comments')) {
      await backend.transaction(async (db) => {
        await db.execute('ALTER TABLE occurrences ADD COLUMN comments TEXT')
      })
    }
    await backend.execute('PRAGMA user_version = 11')
  }
}
